import {
  AssetClass,
  AssetClassEmission,
  BorrowerDetail,
  BorrowerLoan,
} from "../../../models/dto/cat15/cat15.types";

/**
 * Pure derivations for the borrower Summary tab (Figma 7258:26147).
 *
 * The detail endpoint returns the raw loans (principal, latest outstanding and
 * per-year outstanding snapshots) plus summary.yoyTrend (financed tCO₂e per
 * year). Everything the page plots beyond that is derived here, so it can be
 * tested without rendering.
 */

const THB_PER_MILLION = 1_000_000;

/** Σ loans[].principal, THB. */
export const totalPrincipal = (loans: BorrowerLoan[]): number =>
  loans.reduce((sum, l) => sum + (l.principal ?? 0), 0);

/** Σ loans[].outstanding (each loan's latest snapshot), THB. */
export const totalOutstanding = (loans: BorrowerLoan[]): number =>
  loans.reduce((sum, l) => sum + (l.outstanding ?? 0), 0);

/** Calendar year of a YYYY-MM-DD date, or null when it cannot be read. */
const yearOf = (date: string): number | null => {
  const y = Number(String(date).slice(0, 4));
  return Number.isInteger(y) && y > 0 ? y : null;
};

/**
 * Σ principal of the loans originated on or before `year`, THB — what the
 * borrower had drawn by then.
 */
export const principalInYear = (loans: BorrowerLoan[], year: number): number =>
  loans.reduce((sum, l) => {
    const origin = yearOf(l.origination);
    return origin !== null && origin <= year ? sum + (l.principal ?? 0) : sum;
  }, 0);

/** Σ outstanding of every loan's snapshot for `year`, THB (0 when none). */
export const outstandingInYear = (loans: BorrowerLoan[], year: number): number =>
  loans.reduce(
    (sum, l) =>
      sum +
      (l.snapshots ?? [])
        .filter((s) => s.year === year)
        .reduce((s2, s) => s2 + (s.outstanding ?? 0), 0),
    0,
  );

/** Most recent columns the Loan Balance chart shows when it falls back to loan years. */
export const MAX_FALLBACK_YEARS = 10;

/**
 * Years the Loan Balance chart plots. The BE's full yoyTrend window when there is
 * one (the trend chart may show fewer, per its year select); otherwise the contiguous span of the loans'
 * snapshot and origination years, capped to the latest MAX_FALLBACK_YEARS.
 */
export const balanceYears = (borrower: Pick<BorrowerDetail, "loans" | "summary">): number[] => {
  const trendYears = borrower.summary.yoyTrend.map((p) => p.year);
  if (trendYears.length) return [...trendYears].sort((a, b) => a - b);

  const years = borrower.loans.flatMap((l) => [
    ...(l.snapshots ?? []).map((s) => s.year),
    ...[yearOf(l.origination)].filter((y): y is number => y !== null),
  ]);
  if (!years.length) return [];
  const last = Math.max(...years);
  const first = Math.max(Math.min(...years), last - MAX_FALLBACK_YEARS + 1);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
};

export interface LoanBalancePoint {
  year: number;
  /** THB */
  principal: number;
  /** THB */
  outstanding: number;
}

/** Principal (cumulative by origination) and outstanding (snapshots) per year. */
export const loanBalanceSeries = (
  borrower: Pick<BorrowerDetail, "loans" | "summary">,
): LoanBalancePoint[] =>
  balanceYears(borrower).map((year) => ({
    year,
    principal: principalInYear(borrower.loans, year),
    outstanding: outstandingInYear(borrower.loans, year),
  }));

export interface EmissionTrendPoint {
  year: number;
  /** Financed emissions, tCO₂e. */
  financed: number;
  /**
   * tCO₂e per M THB of that year's outstanding; null when the year has no
   * outstanding (no point is drawn, never Infinity/NaN).
   */
  intensity: number | null;
}

/**
 * Financed emissions per year (summary.yoyTrend) with the year's intensity =
 * financed ÷ (Σ snapshot outstanding of that year / 1M).
 */
export const emissionTrendSeries = (
  borrower: Pick<BorrowerDetail, "loans" | "summary">,
): EmissionTrendPoint[] =>
  [...borrower.summary.yoyTrend]
    .sort((a, b) => a.year - b.year)
    .map((p) => {
      const outstanding = outstandingInYear(borrower.loans, p.year);
      return {
        year: p.year,
        financed: p.value,
        intensity:
          outstanding > 0 ? p.value / (outstanding / THB_PER_MILLION) : null,
      };
    });

/** The trend select's "All" value. */
export const ALL_YEARS = 0;

/** The last `years` points (ALL_YEARS or a longer span keeps them all). */
export const lastYears = <T extends { year: number }>(points: T[], years: number): T[] =>
  years > 0 && points.length > years ? points.slice(points.length - years) : points;

/** The SME book's three PCAF asset classes, in Figma order (7846:51918). */
export const ASSET_CLASSES: AssetClass[] = ["listed", "business", "project"];

/**
 * One row per asset class, largest emission first. A class with no loans still
 * gets a zero row, so the card always lists all three; ties keep Figma order.
 */
export const sortedAssetClasses = (rows: AssetClassEmission[]): AssetClassEmission[] =>
  ASSET_CLASSES.map(
    (assetClass) =>
      rows.find((r) => r.assetClass === assetClass) ?? { assetClass, value: 0, percent: 0 },
  ).sort((a, b) => b.value - a.value);

/**
 * Bar opacity by rank, straight from the Figma bars (7846:51945 … 7846:52137):
 * var(--green) at 100 / 80 / 60 / 40 / 30 / 20 / 20 %. Further rows stay at 20 %.
 */
const RANK_OPACITY = [1, 0.8, 0.6, 0.4, 0.3, 0.2];
export const assetClassOpacity = (rank: number): number =>
  RANK_OPACITY[rank] ?? RANK_OPACITY[RANK_OPACITY.length - 1];

/**
 * THB → "150.0" (M THB, one decimal, grouped — Figma "150.0 M THB"). A small
 * non-zero amount that would round to "0.0" keeps up to three decimals instead,
 * so a real balance never reads as nothing.
 */
export const formatMillions = (thb: number): string => {
  const m = thb / THB_PER_MILLION;
  const small = m !== 0 && Math.abs(m) < 0.05;
  return m.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: small ? 3 : 1,
  });
};

/** THB → M THB number, for chart axes. */
export const toMillions = (thb: number): number => thb / THB_PER_MILLION;

/** Smallest axis maximum >= `max` that splits into `steps` round steps. */
export const niceMax = (max: number, steps: number): number => {
  if (!(max > 0) || steps <= 0) return Math.max(steps, 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(max / steps)));
  for (const multiple of [1, 2, 2.5, 5, 10]) {
    const step = magnitude * multiple;
    if (step * steps >= max) return step * steps;
  }
  return Math.ceil(max / steps) * steps;
};
